import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MINIMUM_REQUIRED_SENTENCE_LENGTH } from '../../../../../../constants';
import { cleanNumericInputValue } from '../../../../../../helpers';
import { treatmentRecommendationActions } from '../../../../../../store';
import { FHAntdDateTimePicker, FHInputField, FHTextarea } from '../../../../../base';
import { BAYS } from '../../../../../../constants';
import { useTreatmentPlanEditable } from '../../../hooks/useTreatmentPlanEditable';
import { ERROR_STATE } from '../../constants/general';
import { buildBtProviderNamesFromSources } from './utils/btProviders';
import {
  getRecommendationFieldErrors,
  getRecommendationSchemaForBay,
} from './utils/fieldValidation';
import {
  shouldShowTherapyProvisioningDescription,
  showProvisionDescriptionField,
} from './utils/general';
import { getDisplayProvisioningDescription } from './utils/provisioning';

import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';
import type { RootState } from '../../../../../../store';
import type { IRecommendation, Bays } from '../../../../../../utils/types';
interface RecommendationProps {
  tpBay: Bays;
  onUpdate: (recommendation: IRecommendation | null) => void;
}

function Recommendation(props: RecommendationProps) {
  const { tpBay, onUpdate } = props;
  const dispatch = useDispatch();
  const isEditable = useTreatmentPlanEditable();
  const { recommendation } = useSelector((state: RootState) => state.treatmentRecommendations);
  const { list: providerAssignments = [] } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { coAssignedProviders } = useSelector((state: RootState) => state.providers);

  const btArray = useMemo(
    () => buildBtProviderNamesFromSources(coAssignedProviders, providerAssignments),
    [coAssignedProviders, providerAssignments],
  );

  const recommendationSchema = useMemo(() => getRecommendationSchemaForBay(tpBay), [tpBay]);

  const errors = useMemo(
    () => getRecommendationFieldErrors(recommendation, recommendationSchema),
    [recommendation, recommendationSchema],
  );

  const showProvisionDescription = shouldShowTherapyProvisioningDescription(recommendation);

  const provisioningDescription = useMemo(
    () => getDisplayProvisioningDescription(btArray, recommendation, tpBay),
    [btArray, recommendation, tpBay],
  );

  useEffect(() => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      treatmentRecommendationActions.updateErrorState({
        [ERROR_STATE.RECOMMENDATION]: errorCount,
      }),
    );
  }, [errors, dispatch]);

  const handleFieldChange = (key: string, value: string | number) => {
    dispatch(
      treatmentRecommendationActions.updateState({
        key: 'recommendation',
        data: {
          ...recommendation,
          provision_description: provisioningDescription,
          [key]: value,
        },
      }),
    );
  };

  return (
    <Box>
      <Grid container spacing={2} mb={1}>
        {!showProvisionDescription && (
          <Grid item xs={4.5}>
            <Typography mb={1} fontWeight="bold">
              Describe what was taken into consideration*
            </Typography>
            <FHTextarea
              fullWidth
              minRows={5}
              disabled={!isEditable}
              expandable={isEditable}
              minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
              sx={{ boxShadow: 'none', borderRadius: '8px' }}
              value={recommendation?.considered_factors || ''}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleFieldChange('considered_factors', e.target.value)
              }
              onBlur={() => onUpdate(recommendation)}
              onCloseExpandDialog={() => onUpdate(recommendation)}
              error={errors['considered_factors']}
              helperText={
                errors['considered_factors']
                  ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                  : ''
              }
            />
          </Grid>
        )}
        {showProvisionDescriptionField(showProvisionDescription, tpBay) && (
          <Grid item xs={4.5}>
            <Typography mb={1} fontWeight="bold">
              Therapy provisioning description
            </Typography>
            <FHTextarea
              data-testid="provisionDescriptionTextField"
              fullWidth
              minRows={5}
              disabled={true}
              expandable={false}
              sx={{ boxShadow: 'none', borderRadius: '8px' }}
              minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
              value={provisioningDescription}
              error={errors['provision_description']}
            />
          </Grid>
        )}
        <Grid item xs={tpBay === BAYS.BAY_2_5 ? 7 : 4.5}>
          <Typography mb={1} textAlign="left" fontWeight="bold">
            Recommendations explanation*
          </Typography>
          <FHTextarea
            data-testid="explanationTextField"
            fullWidth
            minRows={5}
            disabled={!isEditable}
            expandable={isEditable}
            minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
            value={recommendation?.explanation}
            onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleFieldChange('explanation', e.target.value)
            }
            onBlur={() => onUpdate(recommendation)}
            onCloseExpandDialog={() => onUpdate(recommendation)}
            error={errors['explanation']}
            helperText={
              errors['explanation'] ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters` : ''
            }
          />
        </Grid>
        <Grid item xs={tpBay === BAYS.BAY_2_5 ? 5 : 3}>
          <Grid container spacing={2}>
            <Grid item xs={tpBay === BAYS.BAY_2_5 ? 6 : 12}>
              <Typography mb={1} textAlign="left" fontWeight="bold">
                Proposed start date*
              </Typography>
              <FHAntdDateTimePicker
                data-testid="startingDatePicker"
                inputHeight={40}
                allowClear={false}
                style={{ width: '100%' }}
                format="MM/DD/YY"
                value={recommendation?.date_start ? moment(recommendation?.date_start) : null}
                disabled={!isEditable}
                error={errors['date_start']}
                onChange={(value: Moment) =>
                  handleFieldChange('date_start', value?.format('YYYY-MM-DD'))
                }
                onSelect={(value: Moment) =>
                  handleFieldChange('date_start', value?.format('YYYY-MM-DD'))
                }
                onBlur={() => onUpdate(recommendation)}
              />
            </Grid>
            <Grid item xs={tpBay === BAYS.BAY_2_5 ? 6 : 12}>
              <Typography fontWeight="bold" textAlign="left" mb={1}>
                Duration (months)*
              </Typography>
              <FHInputField
                data-testid="durationTextField"
                sx={{ minWidth: 50 }}
                inputHeight={40}
                fullWidth
                disabled={!isEditable}
                value={recommendation?.duration || ''}
                onChange={(e) => {
                  handleFieldChange('duration', cleanNumericInputValue(e.target.value));
                }}
                error={errors['duration']}
                onBlur={() => onUpdate(recommendation)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Recommendation;
